/**
 * REDESIGN §2 — the priority model, shared by both systems.
 *
 * Every item the home screen can show gets exactly one bucket, computed from `asOf` (the Israel day
 * of the freshness timestamp) — never from wall-clock time inside a metric (ARCHITECTURE §3), so
 * every function here takes `asOf` explicitly and the golden tests are deterministic.
 *
 *   | bucket  | window            | label        |
 *   | `now`   | today … +3 days   | עכשיו        | + anything already late, + anything failed
 *   | `week`  | +4 … +7 days      | השבוע        |
 *   | `month` | +8 … +30 days     | החודש        |
 *   | `later` | > 30 days, undated| אפשר לחכות   |
 *
 * Within a bucket: date ascending, then amount descending. A late item (`daysUntil < 0`) always
 * sorts first inside `now` and is labelled `באיחור`. An empty bucket is never rendered, so
 * `groupByBucket()` returns only the buckets that have rows.
 */
import { daysBetween, isDayString } from './days.ts';
import { countdownLabel, pastLabel } from './text.ts';
import type { Agorot, DayString, Severity } from './types.ts';

// ───────────────────────────── buckets ─────────────────────────────

export const URGENCY_BUCKETS = ['now', 'week', 'month', 'later'] as const;
export type UrgencyBucket = (typeof URGENCY_BUCKETS)[number];

/** The Hebrew section headings of REDESIGN §2/§4. */
export const URGENCY_BUCKET_LABELS: Readonly<Record<UrgencyBucket, string>> = {
  now: 'עכשיו',
  week: 'השבוע',
  month: 'החודש',
  later: 'אפשר לחכות',
};

/** Last day (inclusive, in `daysUntil`) of each dated window. `later` has no upper bound. */
export const BUCKET_WINDOW_END: Readonly<Record<'now' | 'week' | 'month', number>> = {
  now: 3,
  week: 7,
  month: 30,
};

const BUCKET_RANK: Readonly<Record<UrgencyBucket, number>> = { now: 0, week: 1, month: 2, later: 3 };

// ───────────────────────────── kinds ─────────────────────────────

/**
 * Everything REDESIGN §2 lists as landing in a bucket, in both systems. `title` is written by the
 * server (it names a card, a customer, a category…), so this registry only carries what is
 * structural: the icon, whether a past date means "late", the forced bucket, and the severity.
 */
export const URGENCY_KINDS = [
  // personal (§2 paragraph 3)
  'card_charge',
  'returned_payment',
  'return_fee',
  'category_over_baseline',
  'large_transaction',
  'coverage_shortfall',
  'loan_payment',
  'low_balance',
  // both systems
  'connection_expired',
  'data_stale',
  'refresh_failed',
  // business (§2 paragraph 4)
  'vat_report',
  'vat_payment',
  'tax_advance',
  'national_insurance',
  'invoice_overdue',
  'invoice_due',
  'supplier_standing_order',
  'expense_no_invoice',
  'forecast_negative',
] as const;
export type UrgencyKind = (typeof URGENCY_KINDS)[number];

/**
 * Semantic icon keys. They are NOT the two webs' icon names — `personal/web/src/components/Icon.tsx`
 * and `business/web/src/components/Icon.tsx` ship different sets — so each web maps this vocabulary
 * to its own glyphs (the table lives in `docs/insight-contract.md`).
 */
export const URGENCY_ICONS = [
  'card',
  'alert',
  'trend',
  'receipt',
  'clock',
  'link',
  'wallet',
  'loan',
  'tax',
  'invoice',
  'standing-order',
  'coverage',
] as const;
export type UrgencyIcon = (typeof URGENCY_ICONS)[number];

export interface UrgencyKindSpec {
  icon: UrgencyIcon;
  /**
   * `true` — the date is a deadline, so a past date means the item is LATE (labelled `באיחור`).
   * `false` — the date is when the thing happened; a past date is history, not lateness. A returned
   * standing order is not "late", it already cost money (§2: it is `now` because of `pin`).
   */
  deadline: boolean;
  /** Forced bucket regardless of the date (§2: returns are always `now`, trends are always `later`). */
  pin: UrgencyBucket | null;
  severity: Severity;
}

export const URGENCY_KIND_SPECS: Readonly<Record<UrgencyKind, UrgencyKindSpec>> = {
  // ── personal ──
  /** "card charge dates (`now`/`week`/`month` by `daysUntil`)" */
  card_charge: { icon: 'card', deadline: true, pin: null, severity: 'info' },
  /** "returned standing orders … (always `now` — they already happened and cost money)" */
  returned_payment: { icon: 'alert', deadline: false, pin: 'now', severity: 'warn' },
  /** "…and their fees (always `now`)" */
  return_fee: { icon: 'alert', deadline: false, pin: 'now', severity: 'warn' },
  /** "a category over its baseline (`later`, it is a trend, not an event)" */
  category_over_baseline: { icon: 'trend', deadline: false, pin: 'later', severity: 'warn' },
  /** "a large single transaction (`now` if within 3 days)" — dated, so the window decides. */
  large_transaction: { icon: 'coverage', deadline: false, pin: null, severity: 'info' },
  /** "coverage shortfall (`now`)" */
  coverage_shortfall: { icon: 'coverage', deadline: true, pin: 'now', severity: 'critical' },
  /** "loan payments due (by date)" */
  loan_payment: { icon: 'loan', deadline: true, pin: null, severity: 'info' },
  /** "an unusually low available balance (`now`)" */
  low_balance: { icon: 'wallet', deadline: false, pin: 'now', severity: 'warn' },

  // ── both ──
  /**
   * "consent expiring or expired (`now` if it blocks a refresh, else `later`)" — the server decides
   * which, by passing `pin: 'now'` when the connection can no longer refresh and `pin: 'later'`
   * (the default) while the data is still fresh (§2 `later`, §8 "the EXPIRED banner stops shouting").
   */
  connection_expired: { icon: 'link', deadline: true, pin: 'later', severity: 'warn' },
  /** "expired-consent notices with data still fresh" sit in `later`; staleness is the same class. */
  data_stale: { icon: 'clock', deadline: false, pin: 'later', severity: 'warn' },
  /** "anything failed" → `now`. */
  refresh_failed: { icon: 'alert', deadline: false, pin: 'now', severity: 'warn' },

  // ── business ──
  vat_report: { icon: 'tax', deadline: true, pin: null, severity: 'warn' },
  vat_payment: { icon: 'tax', deadline: true, pin: null, severity: 'warn' },
  tax_advance: { icon: 'tax', deadline: true, pin: null, severity: 'warn' },
  national_insurance: { icon: 'tax', deadline: true, pin: null, severity: 'warn' },
  /** "invoices past due (by age)" — always late by construction, so always `now`. */
  invoice_overdue: { icon: 'invoice', deadline: true, pin: null, severity: 'warn' },
  /** "invoices coming due" */
  invoice_due: { icon: 'invoice', deadline: true, pin: null, severity: 'info' },
  /** "supplier standing orders" */
  supplier_standing_order: { icon: 'standing-order', deadline: true, pin: null, severity: 'info' },
  /** "missing-invoice expenses (`later`)" */
  expense_no_invoice: { icon: 'receipt', deadline: false, pin: 'later', severity: 'info' },
  forecast_negative: { icon: 'trend', deadline: true, pin: null, severity: 'critical' },
};

// ───────────────────────────── item ─────────────────────────────

/** What a server hands in. Everything the registry can supply is optional. */
export interface UrgencyInput {
  /** Stable within one payload — used as the last sort tiebreak and as the React key. */
  id: string;
  kind: UrgencyKind;
  /** The day it lands / is due / happened. `null` = undated → `later` unless pinned (§2). */
  date: DayString | null;
  /** One line, in words (§4.3). Written by the server; never a bare category key. */
  title: string;
  /** Signed agorot, or `null` for an item that carries no figure. `|amount|` drives the sort. */
  amountAgorot?: Agorot | null;
  /** Overrides `URGENCY_KIND_SPECS[kind].icon`. */
  icon?: UrgencyIcon;
  /** Financy `providerId` — the row's `ProviderMark` (§7a). */
  providerId?: string | null;
  /** In-app route, e.g. `#cards/abc`. `null` when the row has no action link. */
  actionHref?: string | null;
  /** Overrides `URGENCY_KIND_SPECS[kind].severity` (a `card_due_soon` turns critical when coverage < 0). */
  severity?: Severity;
  /** §2 "anything failed" — forces `now` whatever the date says. */
  failed?: boolean;
  /**
   * Overrides `URGENCY_KIND_SPECS[kind].pin`. Pass `null` to un-pin a kind that the registry pins
   * (that is how an expired consent that still blocks nothing stays out of `now`).
   */
  pin?: UrgencyBucket | null;
}

/** The bucketed, sorted row the API delivers in `summary.urgency` and the web renders. */
export interface UrgencyItem {
  id: string;
  kind: UrgencyKind;
  bucket: UrgencyBucket;
  date: DayString | null;
  /** `date − asOf` in whole days; negative = past. `null` for an undated item. */
  daysUntil: number | null;
  /** `daysUntil < 0` on a deadline kind. Labelled `באיחור`, sorts first inside `now` (§2). */
  late: boolean;
  title: string;
  amountAgorot: Agorot | null;
  icon: UrgencyIcon;
  providerId: string | null;
  actionHref: string | null;
  severity: Severity;
  /**
   * Ready countdown chip text: `היום` · `מחר` · `בעוד 4 ימים`. A past date reads `באיחור 3 ימים` on a
   * deadline kind and `לפני 3 ימים` on one that merely happened. `null` when undated.
   */
  countdown: string | null;
}

/** One rendered section. Buckets with no rows are absent — "never an empty heading" (§2). */
export interface UrgencyGroup {
  bucket: UrgencyBucket;
  label: string;
  items: UrgencyItem[];
}

// ───────────────────────────── the rules ─────────────────────────────

function pinOf(item: Pick<UrgencyInput, 'kind' | 'pin'>): UrgencyBucket | null {
  // `pin: null` un-pins a kind the registry pins; omitting `pin` (or passing `undefined`) keeps it.
  return item.pin !== undefined ? item.pin : URGENCY_KIND_SPECS[item.kind].pin;
}

/** `date − asOf` in whole days, or `null` when the item is undated / the date is unparsable. */
export function daysUntilOf(item: Pick<UrgencyInput, 'date'>, asOf: DayString): number | null {
  if (!isDayString(item.date)) return null;
  return daysBetween(asOf, item.date);
}

/**
 * REDESIGN §2, in order:
 *   1. an explicit / registry `pin` wins (returns are always `now`, trends always `later`);
 *   2. `failed` → `now` ("anything failed");
 *   3. undated → `later`;
 *   4. `daysUntil < 0` → `now` ("anything already late" — and anything that already happened);
 *   5. the windows: ≤ 3 `now`, ≤ 7 `week`, ≤ 30 `month`, else `later`.
 */
export function bucketOf(item: Pick<UrgencyInput, 'kind' | 'date' | 'failed' | 'pin'>, asOf: DayString): UrgencyBucket {
  const pin = pinOf(item);
  if (pin !== null) return pin;
  if (item.failed === true) return 'now';
  const d = daysUntilOf(item, asOf);
  if (d === null) return 'later';
  if (d < 0) return 'now';
  if (d <= BUCKET_WINDOW_END.now) return 'now';
  if (d <= BUCKET_WINDOW_END.week) return 'week';
  if (d <= BUCKET_WINDOW_END.month) return 'month';
  return 'later';
}

/**
 * §2 "An item that is late (`daysUntil < 0`)". Only a DEADLINE kind can be late: a returned standing
 * order or a large purchase carries a past date because it already happened, and labelling that
 * `באיחור` would be a lie.
 */
export function isLate(item: Pick<UrgencyInput, 'kind' | 'date'>, asOf: DayString): boolean {
  if (!URGENCY_KIND_SPECS[item.kind].deadline) return false;
  const d = daysUntilOf(item, asOf);
  return d !== null && d < 0;
}

/** Resolve one input against the registry and the clock-free `asOf`. */
export function buildItem(input: UrgencyInput, asOf: DayString): UrgencyItem {
  const spec = URGENCY_KIND_SPECS[input.kind];
  const daysUntil = daysUntilOf(input, asOf);
  return {
    id: input.id,
    kind: input.kind,
    bucket: bucketOf(input, asOf),
    date: isDayString(input.date) ? input.date : null,
    daysUntil,
    late: isLate(input, asOf),
    title: input.title,
    amountAgorot: input.amountAgorot ?? null,
    icon: input.icon ?? spec.icon,
    providerId: input.providerId ?? null,
    actionHref: input.actionHref ?? null,
    severity: input.severity ?? spec.severity,
    // The chip must agree with `late`: only a DEADLINE kind may say `באיחור`. A returned standing
    // order or a large purchase carries a past date because it already happened, so it reads
    // `לפני N ימים` (same rule as `isLate` above — the two used to disagree).
    countdown: daysUntil === null ? null : spec.deadline ? countdownLabel(daysUntil) : pastLabel(daysUntil),
  };
}

/**
 * The total order of REDESIGN §2: bucket, then late first, then date ascending (undated last), then
 * amount (magnitude) descending, then `id` so the result is stable. Returns a NEW array.
 */
export function sortItems(items: readonly UrgencyItem[]): UrgencyItem[] {
  return [...items].sort((a, b) => {
    const byBucket = BUCKET_RANK[a.bucket] - BUCKET_RANK[b.bucket];
    if (byBucket !== 0) return byBucket;
    if (a.late !== b.late) return a.late ? -1 : 1;
    if (a.date !== b.date) {
      if (a.date === null) return 1;
      if (b.date === null) return -1;
      return a.date < b.date ? -1 : 1;
    }
    const amountA = Math.abs(a.amountAgorot ?? 0);
    const amountB = Math.abs(b.amountAgorot ?? 0);
    if (amountA !== amountB) return amountB - amountA;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** Build every input and return them in §2 order. This is what a server calls. */
export function buildItems(inputs: readonly UrgencyInput[], asOf: DayString): UrgencyItem[] {
  return sortItems(inputs.map((i) => buildItem(i, asOf)));
}

/** Sorted items → the sections the home screen renders. Empty buckets are omitted (§2). */
export function groupByBucket(items: readonly UrgencyItem[]): UrgencyGroup[] {
  const sorted = sortItems(items);
  const out: UrgencyGroup[] = [];
  for (const bucket of URGENCY_BUCKETS) {
    const rows = sorted.filter((i) => i.bucket === bucket);
    if (rows.length === 0) continue;
    out.push({ bucket, label: URGENCY_BUCKET_LABELS[bucket], items: rows });
  }
  return out;
}

/** `{ now: 2, week: 0, … }` — every bucket, for the `אפשר לחכות` counter and for tests. */
export function countByBucket(items: readonly UrgencyItem[]): Record<UrgencyBucket, number> {
  const out: Record<UrgencyBucket, number> = { now: 0, week: 0, month: 0, later: 0 };
  for (const i of items) out[i.bucket] += 1;
  return out;
}
